import type { ReactNode } from 'react';
import { Lightbulb, Receipt, Sparkles } from 'lucide-react';
import type { EmailPreferences, EmailPreferencesChange } from '../../api/types';
import '../company/company.css';
import './email-choices.css';

/** The words for each kind of mail, shared by the profile sheet and the unsubscribe page. */
export const emailWords = {
  tips: 'Tips and shortcuts',
  tipsHint: 'The cheat sheet: one shortcut at a time, for about two weeks.',
  gettingStarted: 'Getting started',
  gettingStartedHint: 'The welcome, your company’s firsts, and a nudge if things go quiet.',
  account: 'Plan and pages',
  accountHint: 'Always on for admins: your plan, and your pages coming back.',
};

export interface EmailChoicesProps {
  choices: EmailPreferences;
  busy: boolean;
  /** Whether the tips can be turned on here: signed in, yes; from an email's link, only off. */
  canStartTips: boolean;
  onChange: (change: EmailPreferencesChange) => void;
}

/**
 * The three kinds of lifecycle mail as switches (docs/engineering/lifecycle-emails.md, §3): tips and
 * getting started switch; plan and credits mail is listed so nobody wonders, and has no switch.
 */
export function EmailChoices({
  choices,
  busy,
  canStartTips,
  onChange,
}: EmailChoicesProps): ReactNode {
  return (
    <div className="email-choices">
      <Choice
        icon={<Lightbulb size={18} aria-hidden="true" />}
        title={emailWords.tips}
        hint={emailWords.tipsHint}
        on={choices.tips}
        disabled={busy || (!choices.tips && !canStartTips)}
        onFlip={(on) => onChange({ tips: on })}
      />
      <Choice
        icon={<Sparkles size={18} aria-hidden="true" />}
        title={emailWords.gettingStarted}
        hint={emailWords.gettingStartedHint}
        on={choices.gettingStarted}
        disabled={busy}
        onFlip={(on) => onChange({ gettingStarted: on })}
      />
      <Choice
        icon={<Receipt size={18} aria-hidden="true" />}
        title={emailWords.account}
        hint={emailWords.accountHint}
        on
        disabled
        onFlip={() => undefined}
      />
    </div>
  );
}

function Choice(props: {
  icon: ReactNode;
  title: string;
  hint: string;
  on: boolean;
  disabled: boolean;
  onFlip: (on: boolean) => void;
}): ReactNode {
  const { icon, title, hint, on, disabled, onFlip } = props;
  return (
    <label className={`switch-row${on ? ' is-on' : ''}`}>
      {icon}
      <span className="switch-row__copy">
        <span className="switch-row__title">{title}</span>
        <span className="switch-row__hint">{hint}</span>
      </span>
      <input
        type="checkbox"
        role="switch"
        checked={on}
        disabled={disabled}
        onChange={(e) => onFlip(e.currentTarget.checked)}
      />
      <span className="switch" aria-hidden="true" />
    </label>
  );
}
